<template>
  <div>
    <div class="pb-3">
      <!-- create button -->
      <b-button v-has-permission="'ROUTE_CREATE_PERMISSION'" @click="add()" variant="dark">
        <b-icon-plus-circle></b-icon-plus-circle>
        <span class="ml-1">{{ $t('LIST_ADD_LABEL', {catalog: $t('ROUTE')})  | capitalize }}</span>
      </b-button>
      <!-- create button -->

      <!-- refresh button -->
      <b-button class="ml-2" @click="refresh" variant="dark">
        <b-icon-arrow-clockwise></b-icon-arrow-clockwise>
        <span class="ml-1">{{ $t('LIST_REFRESH_LABEL')  | capitalize }}</span>
      </b-button>
      <!-- refresh button -->

      <!-- clear button -->
      <b-button class="ml-1" @click="clear" variant="dark">
        <b-icon-x></b-icon-x>
        <span class="ml-1">{{ $t('LIST_FILTER_CLEAR_LABEL') | capitalize }}</span>
      </b-button>
      <!-- clear button -->

      <!-- Excel export -->
      <download-excel class="ml-2 btn btn-dark"
                      :data="items"
                      :fields="excelFields"
                      :worksheet="labels.module"
                      :name="labels.excel">
        <b-icon-download class="mr-1"></b-icon-download>
        {{ $t('LIST_EXCEL_DOWNLOAD_LABEL') | capitalize }}
      </download-excel>
      <!-- Excel export -->
    </div>
    <div>
      <!-- List page filter -->
      <route-list-filter :only-active="onlyActive" class="d-inline-block w-75" @search="setFilter"
                         v-model="filter"></route-list-filter>
      <!-- List page filter -->

      <!-- Page size -->
      <div class="d-inline-block pl-1 w-25">
        <page-size v-model="size"></page-size>
      </div>
      <!-- Page size -->
    </div>

    <!-- table -->
    <b-table
        id="route-table"
        ref="routesTable"
        striped
        hover
        bordered
        small
        table-variant="dark"
        selectable
        :select-mode="selectMode"
        v-model="items"
        :current-page="page"
        :sort-by="sort"
        :per-page="size"
        :busy.sync="isBusy"
        :fields="fields"
        :filter="filter"
        :items="listAll">
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="ml-2">{{ $t('LIST_LOADING_LABEL', {catalog: $t('ROUTES')})  | capitalize }}</strong>
        </div>
      </template>

      <!-- company -->
      <template #head(company)="">
        <span>{{ labels.company | capitalize }}</span>
      </template>

      <template #cell(company)="data">
        <div style="text-align: left; vertical-align: center">
          <span>{{ data.item.company }}</span>
        </div>
      </template>
      <!-- company -->

      <!-- name -->
      <template #head(name)="">
        <span>{{ labels.name | capitalize }}</span>
      </template>

      <template #cell(name)="data">
        <div style="text-align: left; vertical-align: center">
          <span>{{ data.item.name }}</span>
        </div>
      </template>
      <!-- name -->

      <!-- turn -->
      <template #head(turn)="">
        <span>{{ labels.turn | capitalize }}</span>
      </template>

      <template #cell(turn)="data">
        <div style="text-align: left; vertical-align: center">
          <span>{{ data.item.turn }}</span>
        </div>
      </template>
      <!-- turn -->

      <!-- description -->
      <template #head(description)="">
        <span>{{ labels.description | capitalize }}</span>
      </template>

      <template #cell(description)="data">
        <div style="text-align: left; vertical-align: center">
          <span>{{ data.item.description }}</span>
        </div>
      </template>
      <!-- description -->
      <!-- accounting -->
      <template #head(accounting)="">
        <span>{{ labels.accounting | capitalize }}</span>
      </template>

      <template #cell(accounting)="data">
        <div style="text-align: left; vertical-align: center">
          <div style="text-align: center">
            <b-icon-check-circle variant="success" v-if="data.item.accounting"></b-icon-check-circle>
            <b-icon-x-circle variant="danger" v-else></b-icon-x-circle>
          </div>
        </div>
      </template>
      <!-- accounting -->


      <template #head(enabled)="">
        <span>{{ labels.enabled  | capitalize }}</span>
      </template>

      <template #cell(enabled)="data">
        <div style="text-align: center">
          <b-icon-check-circle variant="success" v-if="data.item.enabled"></b-icon-check-circle>
          <b-icon-x-circle variant="danger" v-else></b-icon-x-circle>
        </div>
      </template>

      <template #head(options)="">
        <span>{{ $t('LIST_OPTIONS_LABEL')  | capitalize }}</span>
      </template>

      <template #cell(options)="data">
        <div style="text-align: center">
          <b-dropdown size="sm" id="dropdown-1" right class="m-md-2">
            <b-dropdown-item v-has-permission="'ROUTE_UPDATE_PERMISSION'" @click="edit(data.item.id)">
              {{ $t('LIST_EDIT_LABEL')  | capitalize }}
            </b-dropdown-item>
            <b-dropdown-item v-has-permission="'ROUTE_DELETE_PERMISSION'"
                             @click="remove(data.item.id, data.item.label)">{{ $t('LIST_DELETE_LABEL')  | capitalize }}
            </b-dropdown-item>
            <b-dropdown-item v-has-permission="'ROUTE_DISABLE_PERMISSION'"
                             @click="disable(data.item.id, data.item.label)" v-if="data.item.enabled">
              {{ $t('LIST_DISABLE_LABEL')  | capitalize }}
            </b-dropdown-item>
            <b-dropdown-item v-has-permission="'ROUTE_ENABLE_PERMISSION'"
                             @click="enable(data.item.id, data.item.label)" v-else>
              {{ $t('LIST_ENABLE_LABEL')  | capitalize }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <!-- table -->
    <b-container fluid>
      <b-row>
        <b-col>
          <b-pagination
              v-model="page"
              :total-rows="totalRows"
              :per-page="size"
              pills
              aria-controls="route-table">
          </b-pagination>
        </b-col>
        <b-col class="text-right">
          {{ (pageTotals.size ? $t('GRID_ROW_TOTAL', pageTotals) : $t('GRID_ALL_ROWS_TOTAL')) | capitalize }}
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {Form, ProcessWithLoadingAndMessage, Table} from "@/mixins";
import Routes from "@/routes";
import eventBus from "@/events";
import RouteListFilter from "@/routes/RouteListFilter";
import PageSize from "@/components/PageSize";

export default {
  name: "RouteList",
  props: ['selectMode', 'currentlySelected', 'onlyActive'],
  components: {PageSize, RouteListFilter},
  mixins: [Table, Form, ProcessWithLoadingAndMessage],
  mounted() {
    eventBus.$on('LIST_COUNT_ROUTE_EVENT', (totalElements) => {
      this.totalRows = totalElements;
    });
  },
  methods: {},
  data() {
    return {
      labels: {
        company: this.$t('ROUTE_COMPANY_LABEL'),
        turn: this.$t('ROUTE_TURN_LABEL'),
        name: this.$t('ROUTE_NAME_LABEL'),
        description: this.$t('ROUTE_DESCRIPTION_LABEL'),
        accounting: this.$t('ROUTE_ACCOUNTING_LABEL'),
        enabled: this.$t('LIST_ENABLED_LABEL'),
        module: this.$t('ROUTES'),
        excel: this.$t('ROUTES') + '.xls',
      },
      registerPage: 'ROUTE_REGISTER_TITLE',
      editPage: 'ROUTE_EDIT_TITLE',
      selectedCount: 0,
      allSelected: false,
      originals: []
    }
  },
  computed: {
    controller() {
      return Routes;
    },
    fields() {
      return [
        {key: 'company', sortable: true, tdClass: 'customTd'},
        {key: 'name', sortable: true, tdClass: 'customTd'},
        {key: 'turn', sortable: true, tdClass: 'customTd'},
        {
          key: 'description',
          sortable: true,
          tdClass: 'customTd'
        }, {key: 'accounting', sortable: true, tdClass: 'customTd'},
        {key: 'enabled', sortable: true, tdClass: 'enabledTd'},
        {key: 'options', sortable: false, tdClass: 'optionsTd'}];
    },
    tableRef() {
      return this.$refs.routesTable;
    },
    excelFields() {
      const fields = {};
      fields[this.labels.company] = 'company';
      fields[this.labels.name] = 'name';
      fields[this.labels.turn] = 'turn'
      fields[this.labels.description] = 'description';
      fields[this.labels.accounting] = 'accounting';
      return fields;
    }
  }
}
</script>

<style scoped>
.selectTd {
  width: 15px !important;
}

.nameTd {
  width: 75% !important;
}
</style>
